import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Navbar,
  Nav,
  Dropdown,
  Badge,
  Modal,
  Form,
  Spinner,
  Alert,
  Table,
} from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useHighlightedStores } from '../../context/HighlightedStoresContext';
import { useLanguage } from '../../context/LanguageContext';
import { Logo } from '../Logo';
import { AddStoreModal } from '../Store';
import { ReturnsTable } from '../Returns';
import {
  AdminOrdersPage,
  AdminWBStoreReturnsPage,
  AdminConsolePage,
  WarehouseReturnsPage,
  FindReturnByShkModal,
} from '../Admin';
import SuperuserHighlightStoresModal from '../Admin/SuperuserHighlightStoresModal';
import CreateSuperuserModal from '../Admin/CreateSuperuserModal';
import { BillingsPage, AdminBillingsPage } from '../Billings';
import { SupportChatList } from '../Support';
import { LanguageThemeSelector, BackButton, Footer } from '../common';
import SystemNotificationsMenu from '../common/SystemNotificationsMenu';
import { MARKETPLACE_ICONS } from '../../assets/marketplaceIcons';
import ChangePassword from './ChangePassword';
import Profile from './Profile';
import api from '../../services/api';
import { useOrdersWebSocket } from '../../hooks/useOrdersWebSocket';
import {
  SUPPORT_UNREAD_POLL_INTERVAL_MS,
  BILLING_UNREAD_POLL_INTERVAL_MS,
} from '../../constants/polling';

const Dashboard = () => {
  const { user, logout, impersonate: authImpersonate, exitImpersonation, isImpersonating } = useAuth();
  const { highlightedStores } = useHighlightedStores();
  const { t, isRussian } = useLanguage();
  // For superuser, start with adminOrders view
  const [activeView, setActiveView] = useState(user?.is_superuser ? 'adminOrders' : 'dashboard');
  const [previousView, setPreviousView] = useState(user?.is_superuser ? 'adminOrders' : 'dashboard'); // Track where user came from
  const [showAddStore, setShowAddStore] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null); // null means "All Stores"
  
  // Support chat unread count
  const [unreadSupportCount, setUnreadSupportCount] = useState(0);
  // Accounts (billing) unread count — new expenses since last open
  const [unreadBillingCount, setUnreadBillingCount] = useState(0);
  
  // Support chat notifications refs (kept for potential future use)
  const supportNotificationTimeoutRef = useRef({});

  // Edit store modal state
  const [showEditStore, setShowEditStore] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [editStoreName, setEditStoreName] = useState('');
  const [editStoreLoading, setEditStoreLoading] = useState(false);
  const [editStoreError, setEditStoreError] = useState('');

  // Delete store modal state
  const [showDeleteStore, setShowDeleteStore] = useState(false);
  const [deletingStore, setDeletingStore] = useState(null);
  const [deleteStoreLoading, setDeleteStoreLoading] = useState(false);

  // Update API key modal state
  const [showUpdateApiKey, setShowUpdateApiKey] = useState(false);
  const [updatingStore, setUpdatingStore] = useState(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [updateApiKeyLoading, setUpdateApiKeyLoading] = useState(false);
  const [updateApiKeyError, setUpdateApiKeyError] = useState('');
  const [updateApiKeySuccess, setUpdateApiKeySuccess] = useState('');

  // Returns table state - default to 'inTransit'
  const [returnsTab, setReturnsTab] = useState('inTransit');

  // Admin: Find return by shkId modal
  const [showFindReturnByShkModal, setShowFindReturnByShkModal] = useState(false);
  const [showHighlightStoresModal, setShowHighlightStoresModal] = useState(false);
  const [showCreateSuperuserModal, setShowCreateSuperuserModal] = useState(false);
  // Admin: open order edit from "Find return by shkId" (order id to open in AdminOrdersPage)
  const [initialEditOrderId, setInitialEditOrderId] = useState(null);
  // Impersonation modal
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [impersonateSearch, setImpersonateSearch] = useState('');
  const [impersonateUsers, setImpersonateUsers] = useState([]);
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const [impersonateSubmitting, setImpersonateSubmitting] = useState(false);
  const [impersonateError, setImpersonateError] = useState('');
  // Support: pre-created chat to open immediately (e.g. "Send receipt" from Billings)
  const [supportInitialChat, setSupportInitialChat] = useState(null);

  // Navigate to a new view while tracking history
  const navigateTo = (view) => {
    if (view !== activeView) {
      setPreviousView(activeView);
      setActiveView(view);
    }
  };

  // Go back to previous view
  const goBack = () => {
    setActiveView(previousView);
    setPreviousView('dashboard'); // Reset to dashboard after going back
  };

  // Statistics state - loaded from API
  const [stats, setStats] = useState({
    inTransit: { items: 0, ruble: 0 },
    otherPvzInTransit: { items: 0, ruble: 0 },
    inProcessing: { items: 0, ruble: 0 },
    pendingDecision: { items: 0, ruble: 0 },
    completed: { items: 0, ruble: 0 },
  });

  const loadStatistics = useCallback(async () => {
    try {
      // Pass store_id filter if a specific store is selected
      const selectedStoreObj = stores.find(s => s.id === selectedStore);
      const params = selectedStore && selectedStoreObj ? { store_id: selectedStoreObj.store_id } : {};
      const response = await api.getReturnsStatistics(params);
      if (response.data) {
        setStats({
          inTransit: { 
            items: response.data.in_transit, 
            ruble: response.data.in_transit_rub || 0 
          },
          otherPvzInTransit: {
            items: response.data.other_pvz_in_transit ?? 0,
            ruble: response.data.other_pvz_in_transit_rub || 0,
          },
          inProcessing: { 
            items: response.data.in_processing, 
            ruble: response.data.in_processing_rub || 0 
          },
          pendingDecision: { 
            items: response.data.pending_decision, 
            ruble: response.data.pending_decision_rub || 0 
          },
          completed: { 
            items: response.data.completed, 
            ruble: response.data.completed_rub || 0 
          },
        });
      }
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  }, [selectedStore, stores]);

  const loadStores = useCallback(async () => {
    try {
      const response = await api.getStores();
      setStores(response || []);
    } catch (err) {
      console.error('Failed to load stores:', err);
    }
  }, []);

  // Track the currently selected chat ID and its unread count for accurate badge updates
  const selectedChatInfoRef = useRef({ chatId: null, unreadCount: 0 });
  
  // Load unread support messages count and check for new messages
  const loadUnreadSupportCount = useCallback(async (subtractCount = 0, immediateUpdate = false) => {
    try {
      // If immediateUpdate is true, update locally first without waiting for server
      if (immediateUpdate && subtractCount > 0) {
        setUnreadSupportCount(prev => Math.max(0, prev - subtractCount));
      }
      
      // Load unread count from server
      const countResponse = await api.getSupportChatUnreadCount();
      if (countResponse.data) {
        const serverCount = countResponse.data.unread_count || 0;
        // Always subtract the unread count of the selected chat (if any)
        const selectedChatUnreadCount = selectedChatInfoRef.current.unreadCount || 0;
        const totalSubtract = Math.max(subtractCount, selectedChatUnreadCount);
        // If subtractCount is provided, use it (it's more recent), otherwise use selected chat info
        setUnreadSupportCount(Math.max(0, serverCount - totalSubtract));
      }
      
      // Notifications are disabled - no need to load chats for notifications
    } catch (err) {
      console.error('Failed to load unread support count:', err);
    }
  }, []);
  
  // Callback to update selected chat info
  const handleSelectedChatChange = useCallback((chatId, unreadCount) => {
    const previousUnreadCount = selectedChatInfoRef.current.unreadCount || 0;
    selectedChatInfoRef.current = { chatId, unreadCount: unreadCount || 0 };
    
    // If unread count decreased (messages were read), update badge immediately
    if (previousUnreadCount > unreadCount) {
      const countToSubtract = previousUnreadCount - unreadCount;
      setUnreadSupportCount(prev => Math.max(0, prev - countToSubtract));
    } else if (unreadCount === 0 && previousUnreadCount > 0) {
      // All messages in chat are now read
      setUnreadSupportCount(prev => Math.max(0, prev - previousUnreadCount));
    }
    
    // Also sync with server
    loadUnreadSupportCount(unreadCount || 0, false);
  }, [loadUnreadSupportCount]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  // Poll for unread support messages (редко + только когда вкладка видна — меньше RPS).
  // На экране support/adminSupport счётчик обновляет SupportChatList — не дублируем запросы.
  useEffect(() => {
    if (activeView === 'support' || activeView === 'adminSupport') {
      return undefined;
    }
    const run = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      loadUnreadSupportCount();
    };
    run();
    const interval = setInterval(run, SUPPORT_UNREAD_POLL_INTERVAL_MS);
    const onVis = () => {
      if (!document.hidden) loadUnreadSupportCount();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [loadUnreadSupportCount, activeView]);

  // Load and poll billing unread count (for non-superuser)
  const loadUnreadBillingCount = useCallback(async () => {
    if (user?.is_superuser) return;
    try {
      const res = await api.getBillingUnreadCount();
      setUnreadBillingCount(res?.data?.unread_count ?? 0);
    } catch (err) {
      console.error('Failed to load billing unread count:', err);
    }
  }, [user?.is_superuser]);

  useEffect(() => {
    const run = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      loadUnreadBillingCount();
    };
    run();
    const interval = setInterval(run, BILLING_UNREAD_POLL_INTERVAL_MS);
    const onVis = () => {
      if (!document.hidden) loadUnreadBillingCount();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [loadUnreadBillingCount]);

  // Check URL for chat parameter and navigate to support view
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const chatId = urlParams.get('chat');
    if (chatId) {
      // Navigate to support view (adminSupport for superusers, support for regular users)
      const supportView = user?.is_superuser ? 'adminSupport' : 'support';
      if (activeView !== supportView) {
        setActiveView(supportView);
      }
    }
  }, [user?.is_superuser, activeView]); // Run when user or activeView changes

  // Deep link: ?view=billings → Accounts (filters: accounts_search, accounts_nm, accounts_store)
  useEffect(() => {
    if (!user) return;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('view') !== 'billings') return;
    if (!user.can_see_accounts && !user.is_superuser) return;
    setActiveView('billings');
  }, [user]);
  
  // Handle support notification add (from SupportChatList) - notifications disabled but kept for future use
  const handleSupportNotificationAdd = useCallback(() => {
    // Notifications are currently disabled
  }, []);
  
  // Cleanup notification timeouts on unmount
  useEffect(() => {
    const timeouts = supportNotificationTimeoutRef.current;
    return () => {
      Object.values(timeouts).forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  // Reload statistics when stores or selected store changes
  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  // WebSocket handlers for real-time order updates
  // When an order is updated, reload statistics to update counters
  const handleOrderUpdate = useCallback((updatedOrder) => {
    // Reload statistics when order status changes
    // This ensures counters are updated when order moves between categories
    loadStatistics();
  }, [loadStatistics]);

  const handleOrderCreated = useCallback((newOrder) => {
    // Reload statistics when a new order is created
    loadStatistics();
  }, [loadStatistics]);

  // WebSocket push отключён: счётчики на дашборде не перезагружаются сами при изменении заказов
  useOrdersWebSocket(handleOrderUpdate, handleOrderCreated, false);

  // Poll for pending store verification status (реже + не в фоновой вкладке)
  useEffect(() => {
    const pendingStores = stores.filter(s => s.verification_status === 'pending');
    if (pendingStores.length === 0) return;

    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      for (const store of pendingStores) {
        try {
          const response = await api.getStoreVerificationStatus(store.id);
          if (response.verification_status !== 'pending') {
            loadStores();
            break;
          }
        } catch (err) {
          console.error('Failed to poll store status:', err);
        }
      }
    };

    tick();
    const pollInterval = setInterval(tick, 15_000);
    const onVis = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [stores, loadStores]);

  const handleStoreAdded = (newStore) => {
    setStores((prev) => [...prev, newStore]);
  };

  const handleEditStore = (store, e) => {
    e.stopPropagation(); // Prevent card selection
    setEditingStore(store);
    setEditStoreName(store.name || '');
    setEditStoreError('');
    setShowEditStore(true);
  };

  const handleDeleteStore = (store, e) => {
    e.stopPropagation(); // Prevent card selection
    setDeletingStore(store);
    setShowDeleteStore(true);
  };

  const confirmDeleteStore = async () => {
    if (!deletingStore) return;

    setDeleteStoreLoading(true);
    try {
      await api.deleteStore(deletingStore.id);
      // Remove from local state
      setStores((prev) => prev.filter((s) => s.id !== deletingStore.id));
      setShowDeleteStore(false);
      setDeletingStore(null);
    } catch (err) {
      console.error('Failed to delete store:', err);
    } finally {
      setDeleteStoreLoading(false);
    }
  };

  const handleSaveStoreName = async () => {
    if (!editingStore) return;

    setEditStoreLoading(true);
    setEditStoreError('');

    try {
      await api.updateStore(editingStore.id, {
        name: editStoreName.trim() || `Store ${editingStore.store_id}`,
      });
      
      // Update the store in local state
      setStores((prev) =>
        prev.map((s) =>
          s.id === editingStore.id
            ? { ...s, name: editStoreName.trim() || `Store ${editingStore.store_id}` }
            : s
        )
      );
      
      setShowEditStore(false);
      setEditingStore(null);
    } catch (err) {
      const errorText = err.data?.errorText || err.data?.detail;
      if (typeof errorText === 'string') {
        setEditStoreError(errorText);
      } else if (typeof errorText === 'object') {
        const messages = Object.values(errorText).flat();
        setEditStoreError(messages.join(' '));
      } else {
        setEditStoreError(t('stores.editError'));
      }
    } finally {
      setEditStoreLoading(false);
    }
  };

  const handleUpdateApiKey = (store, e) => {
    e.stopPropagation(); // Prevent card selection
    setUpdatingStore(store);
    setNewApiKey('');
    setUpdateApiKeyError('');
    setUpdateApiKeySuccess('');
    setShowUpdateApiKey(true);
  };

  const handleSaveApiKey = async () => {
    if (!updatingStore || !newApiKey.trim()) return;

    setUpdateApiKeyLoading(true);
    setUpdateApiKeyError('');
    setUpdateApiKeySuccess('');

    try {
      const response = await api.updateStoreApiKey(updatingStore.id, newApiKey.trim());
      
      setUpdateApiKeySuccess(t('stores.apiKeyUpdateSuccess'));
      
      // Reload stores to get updated status
      await loadStores();
      
      setTimeout(() => {
        setShowUpdateApiKey(false);
        setUpdatingStore(null);
        setNewApiKey('');
        setUpdateApiKeySuccess('');
      }, 1500);
    } catch (err) {
      const errorText = err.data?.errorText || err.data?.api_key;
      if (typeof errorText === 'string') {
        setUpdateApiKeyError(errorText);
      } else if (Array.isArray(errorText)) {
        setUpdateApiKeyError(errorText[0]);
      } else if (typeof errorText === 'object') {
        const messages = Object.values(errorText).flat();
        setUpdateApiKeyError(messages.join(' '));
      } else {
        setUpdateApiKeyError(t('stores.apiKeyUpdateError'));
      }
    } finally {
      setUpdateApiKeyLoading(false);
    }
  };

  const loadImpersonateUsers = useCallback(async (searchOverride) => {
    const q = searchOverride !== undefined ? searchOverride : impersonateSearch;
    setImpersonateLoading(true);
    setImpersonateError('');
    try {
      const res = await api.getImpersonateUsers(q);
      setImpersonateUsers(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setImpersonateError(err.message || t('impersonate.error'));
      setImpersonateUsers([]);
    } finally {
      setImpersonateLoading(false);
    }
  }, [impersonateSearch, t]);

  const handleOpenImpersonateModal = useCallback(() => {
    setShowImpersonateModal(true);
    setImpersonateSearch('');
    setImpersonateUsers([]);
    setImpersonateError('');
    setImpersonateLoading(true);
    api.getImpersonateUsers('').then((res) => {
      setImpersonateUsers(Array.isArray(res?.data) ? res.data : []);
    }).catch(() => setImpersonateUsers([])).finally(() => setImpersonateLoading(false));
  }, []);

  const handleImpersonateSelect = useCallback(async (targetUser) => {
    setImpersonateSubmitting(true);
    setImpersonateError('');
    try {
      const res = await api.impersonate(targetUser.id);
      const data = res?.data;
      if (data?.token && data?.user) {
        authImpersonate(data.token, data.user);
        setShowImpersonateModal(false);
      } else {
        setImpersonateError(t('impersonate.error'));
      }
    } catch (err) {
      setImpersonateError(err?.data?.errorText || err?.message || t('impersonate.error'));
    } finally {
      setImpersonateSubmitting(false);
    }
  }, [authImpersonate, t]);

  const getStoreIcon = (storeType) => {
    return MARKETPLACE_ICONS[storeType] || null;
  };

  const getSelectedStoreName = () => {
    if (!selectedStore) {
      return t('nav.allStores');
    }
    const store = stores.find((s) => s.id === selectedStore);
    return store?.name || `Store ${store?.store_id}`;
  };

  const getSelectedStoreIcon = () => {
    if (!selectedStore) return null;
    const store = stores.find((s) => s.id === selectedStore);
    return store ? getStoreIcon(store.store_type) : null;
  };

  const StatCard = ({ title, subtitle, stats: cardStats, buttonText, onClick }) => (
    <Card className="stat-card h-100 border-0">
      <Card.Body className="d-flex flex-column">
        <div className="text-center mb-3">
          <h6 className="fw-bold mb-1">{title}</h6>
          {subtitle && <small className="text-secondary">{subtitle}</small>}
        </div>
        <div className="text-center flex-grow-1">
          <div className="stat-value">{cardStats.items} {t('dashboard.items')}</div>
          <div className="stat-or">{t('dashboard.or')}</div>
          <div className="stat-currency">
            {cardStats.ruble > 0 
              ? `${Math.round(cardStats.ruble).toLocaleString()} ${t('common.currency.ruble')}`
              : `— ${t('common.currency.ruble')}`
            }
          </div>
        </div>
      </Card.Body>
      <Card.Footer className="bg-transparent border-0 p-3 pt-0">
        <Button className="btn-orange w-100" onClick={onClick}>
          {buttonText}
        </Button>
      </Card.Footer>
    </Card>
  );

  const renderDashboard = () => {
    // Superuser should not see the regular dashboard
    if (user?.is_superuser) {
      return null; // Will be handled by renderContent
    }
    
    return (
    <>
    <Container className="py-4 content-container">
      <div className="dashboard-stat-cards-row mb-4">
        <StatCard
          title={t('dashboard.inTransit')}
          subtitle={isRussian ? 'ИндигоСклад' : 'IndigoWarehouse'}
          stats={stats.inTransit}
          buttonText={t('dashboard.viewItemsInTransit')}
          onClick={() => {
            setReturnsTab('inTransit');
            navigateTo('returns');
          }}
        />
        <StatCard
          title={t('returns.returnsOtherPvz')}
          stats={stats.otherPvzInTransit}
          buttonText={t('dashboard.viewReturnsOtherPvz')}
          onClick={() => {
            setReturnsTab('otherPvz');
            navigateTo('returns');
          }}
        />
        <StatCard
          title={t('dashboard.ordersInProcessing')}
          stats={stats.inProcessing}
          buttonText={t('dashboard.viewProcessingOrders')}
          onClick={() => {
            setReturnsTab('inProcessing');
            navigateTo('returns');
          }}
        />
        <StatCard
          title={t('dashboard.ordersPendingDecision')}
          stats={stats.pendingDecision}
          buttonText={t('dashboard.processReturns')}
          onClick={() => {
            setReturnsTab('pendingDecision');
            navigateTo('returns');
          }}
        />
        <StatCard
          title={t('dashboard.completedOrders')}
          stats={stats.completed}
          buttonText={t('dashboard.viewCompletedOrders')}
          onClick={() => {
            setReturnsTab('completed');
            navigateTo('returns');
          }}
        />
      </div>
      
      {/* Stores Section */}
      {stores.length > 0 && (
        <Card className="mt-4 border-0 shadow-sm">
          <Card.Header className="bg-transparent border-bottom">
            <h5 className="mb-0 fw-bold">{t('stores.title')}</h5>
          </Card.Header>
          <Card.Body>
            {/* Failed verification alert */}
            {stores.some(s => s.verification_status === 'failed') && (
              <Alert variant="danger" className="mb-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {t('stores.verificationFailedMessage')}
              </Alert>
            )}
            <Row className="g-3">
              {stores.map((store) => (
                <Col key={store.id} xs={12} sm={6} md={4} lg={3}>
                  <Card 
                    className={`h-100 position-relative ${store.verification_status === 'failed' ? 'border-danger' : ''}`}
                    style={{ 
                      cursor: store.verification_status === 'verified' ? 'pointer' : 'default',
                      border: selectedStore === store.id ? '2px solid var(--orange-primary)' : 
                              store.verification_status === 'failed' ? '2px solid #dc3545' : undefined,
                      opacity: store.verification_status === 'pending' ? 0.8 : 1
                    }}
                    onClick={() => store.verification_status === 'verified' && setSelectedStore(selectedStore === store.id ? null : store.id)}
                  >
                    {/* Store action buttons */}
                    <div 
                      className="position-absolute d-flex gap-1"
                      style={{ top: '0.5rem', right: '0.5rem', zIndex: 10 }}
                    >
                      {/* Edit button - for all stores */}
                      <Button
                        variant="link"
                        size="sm"
                        className="p-1"
                        onClick={(e) => handleEditStore(store, e)}
                        title={t('stores.edit')}
                      >
                        <i className="bi bi-pencil text-secondary"></i>
                      </Button>
                      {/* Update API key button - for all stores */}
                      <Button
                        variant="link"
                        size="sm"
                        className="p-1"
                        onClick={(e) => handleUpdateApiKey(store, e)}
                        title={t('stores.updateApiKey')}
                      >
                        <i className="bi bi-key text-primary"></i>
                      </Button>
                      {/* Delete button - for all stores */}
                      <Button
                        variant="link"
                        size="sm"
                        className="p-1"
                        onClick={(e) => handleDeleteStore(store, e)}
                        title={t('stores.deleteStore')}
                      >
                        <i className="bi bi-trash text-danger"></i>
                      </Button>
                    </div>
                    <Card.Body className="text-center">
                      <img
                        src={getStoreIcon(store.store_type)}
                        alt={store.store_type}
                        style={{ width: 48, height: 48, objectFit: 'contain', marginBottom: '0.5rem', borderRadius: '8px' }}
                      />
                      <h6 className="mb-1">{store.name || `Store ${store.store_id}`}</h6>
                      <small className="text-secondary d-block">
                        {t('stores.storeId')}: {store.store_id}
                      </small>
                      {/* Status badge based on verification */}
                      {store.verification_status === 'verified' && (
                        <Badge bg="success" className="mt-2">
                          {t('stores.connected')}
                        </Badge>
                      )}
                      {store.verification_status === 'pending' && (
                        <Badge bg="warning" text="dark" className="mt-2">
                          <Spinner animation="border" size="sm" className="me-1" style={{ width: '0.7rem', height: '0.7rem' }} />
                          {t('stores.verifying')}
                        </Badge>
                      )}
                      {store.verification_status === 'failed' && (
                        <Badge bg="danger" className="mt-2">
                          <i className="bi bi-x-circle me-1"></i>
                          {t('stores.verificationFailed')}
                        </Badge>
                      )}
                      {/* Show error message for failed stores */}
                      {store.verification_status === 'failed' && store.verification_error && (
                        <small className="text-danger d-block mt-1">
                          {store.verification_error}
                        </small>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      )}
    </Container>

      {/* All stores returns: full viewport width (outside content-container max-width) */}
      <div className="dashboard-all-returns-fullbleed mt-4">
        <ReturnsTable
          activeTab="all"
          onTabChange={setReturnsTab}
          selectedStoreId={stores.find(s => s.id === selectedStore)?.store_id}
          hideTabNavigation={true}
        />
      </div>
    </>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'profile':
        return (
          <>
            <Container className="py-3 content-container">
              <BackButton onClick={goBack} />
            </Container>
            <Profile />
          </>
        );
      case 'security':
        return (
          <Container className="py-4 content-container">
            <BackButton onClick={goBack} className="mb-3" />
            <Row className="justify-content-center">
              <Col lg={6}>
                <ChangePassword />
              </Col>
            </Row>
          </Container>
        );
      case 'returns':
        return (
          <Container fluid className="py-4 px-0">
            <div className="px-3 px-md-4 mb-3">
              <BackButton onClick={goBack} />
            </div>
            <div className="dashboard-all-returns-fullbleed">
              <ReturnsTable
                activeTab={returnsTab}
                onTabChange={setReturnsTab}
                selectedStoreId={stores.find(s => s.id === selectedStore)?.store_id}
              />
            </div>
          </Container>
        );
      case 'goodsOnSale':
        return (
          <Container fluid className="py-4 px-0">
            <div className="px-3 px-md-4 mb-3">
              <BackButton onClick={goBack} className="mb-2" />
              <h4 className="mb-0 fw-bold">{t('nav.goodsOnSale')}</h4>
            </div>
            <div className="dashboard-all-returns-fullbleed">
              <ReturnsTable
                activeTab="whSale"
                onTabChange={() => {}}
                selectedStoreId={stores.find(s => s.id === selectedStore)?.store_id}
                hideTabNavigation
              />
            </div>
          </Container>
        );
      case 'adminOrders':
        return (
          <Container className="py-4 admin-orders-view px-2 px-md-3" fluid>
            {!user?.is_superuser && <BackButton onClick={goBack} className="mb-3" />}
            <AdminOrdersPage
              initialEditOrderId={initialEditOrderId}
              onClearInitialEditOrderId={() => setInitialEditOrderId(null)}
            />
          </Container>
        );
      case 'support':
        return (
          <Container fluid className="py-4 content-container-wide px-3 px-md-4">
            <BackButton onClick={goBack} className="mb-3" />
            <SupportChatList 
              isAdmin={false} 
              onUnreadCountChange={loadUnreadSupportCount}
              onNotificationAdd={handleSupportNotificationAdd}
              onSelectedChatChange={handleSelectedChatChange}
              initialChat={supportInitialChat}
              onInitialChatConsumed={() => setSupportInitialChat(null)}
            />
          </Container>
        );
      case 'adminSupport':
        return (
          <Container fluid className="py-4 content-container-wide px-3 px-md-4">
            {!user?.is_superuser && <BackButton onClick={goBack} className="mb-3" />}
            <SupportChatList 
              isAdmin={true} 
              onUnreadCountChange={loadUnreadSupportCount}
              onNotificationAdd={handleSupportNotificationAdd}
              onSelectedChatChange={handleSelectedChatChange}
            />
          </Container>
        );
      case 'warehouseReturns':
        return (
          <Container fluid className="py-4 content-container-wide px-3 px-md-4">
            {!user?.is_superuser && <BackButton onClick={goBack} className="mb-3" />}
            <WarehouseReturnsPage />
          </Container>
        );
      case 'billings':
        if (!user?.can_see_accounts && !user?.is_superuser) {
          return renderDashboard();
        }
        return (
          <Container fluid className="py-3 py-md-4 billings-view-full px-2 px-sm-3">
            <BackButton onClick={goBack} className="mb-3" />
            <BillingsPage
              onOpen={() => { api.billingMarkSeen().then(() => setUnreadBillingCount(0)); }}
              onNavigateToSupport={async (opts) => {
                if (opts?.subject) {
                  const subjectNorm = opts.subject.trim().toLowerCase();
                  try {
                    const listRes = await api.getSupportChats();
                    const chats = listRes?.data || [];
                    const existing = chats.find(
                      (c) => (c.subject || '').trim().toLowerCase() === subjectNorm
                    );
                    if (existing) {
                      setSupportInitialChat(existing);
                    } else {
                      const res = await api.createSupportChat({ subject: opts.subject });
                      if (res?.data) setSupportInitialChat(res.data);
                    }
                  } catch (_) { /* error shown in SupportChatList if needed */ }
                }
                navigateTo('support');
              }}
            />
          </Container>
        );
      case 'adminBillings':
        return (
          <Container fluid className="py-4 content-container-wide px-3 px-md-4">
            {!user?.is_superuser && <BackButton onClick={goBack} className="mb-3" />}
            <AdminBillingsPage />
          </Container>
        );
      case 'adminWbReturns':
        return (
          <Container fluid className="py-4 admin-orders-view">
            {!user?.is_superuser && <BackButton onClick={goBack} className="mb-3" />}
            <AdminWBStoreReturnsPage />
          </Container>
        );
      case 'adminConsole':
        if (!user?.is_superuser) {
          return renderDashboard();
        }
        return (
          <Container fluid className="py-4 content-container-wide px-3 px-md-4">
            <AdminConsolePage />
          </Container>
        );
      default:
        return renderDashboard();
    }
  };

  const selectedStoreIcon = getSelectedStoreIcon();

  return (
    <div className="dashboard-wrapper">
      {/* Navigation Header */}
      <Navbar 
        className="dashboard-navbar shadow-sm sticky-top px-3"
        style={{ backgroundColor: 'var(--navbar-bg)' }}
      >
        <Container fluid>
          {/* Logo */}
          <Navbar.Brand 
            href="#" 
            onClick={() => {
              setActiveView('dashboard');
              setPreviousView('dashboard');
            }}
            style={{ marginRight: '1rem' }}
          >
            <Logo size="medium" />
          </Navbar.Brand>

          {/* Navigation Items */}
          <Nav className="mx-auto d-none d-lg-flex gap-2 align-items-center">
            {user?.is_superuser ? (
              // Superuser navigation - only admin sections
              <>
                <Button
                  variant={activeView === 'adminOrders' ? 'warning' : 'outline-warning'}
                  className="nav-pill-btn"
                  onClick={() => navigateTo('adminOrders')}
                >
                  <i className="bi bi-shield-lock me-1"></i>
                  {t('nav.adminOrders')}
                </Button>
                <Button
                  variant={activeView === 'adminWbReturns' ? 'warning' : 'outline-warning'}
                  className="nav-pill-btn"
                  onClick={() => navigateTo('adminWbReturns')}
                >
                  <i className="bi bi-cloud-download me-1"></i>
                  {t('nav.adminWbReturns')}
                </Button>
                <Button
                  variant={activeView === 'adminSupport' ? 'warning' : 'outline-warning'}
                  className="nav-pill-btn position-relative"
                  onClick={() => navigateTo('adminSupport')}
                >
                  <i className="bi bi-headset me-1"></i>
                  {t('nav.adminSupport')}
                  {unreadSupportCount > 0 && (
                    <Badge 
                      bg="danger" 
                      pill 
                      className="position-absolute"
                      style={{ top: '-5px', right: '-5px', fontSize: '0.65rem' }}
                    >
                      {unreadSupportCount > 99 ? '99+' : unreadSupportCount}
                    </Badge>
                  )}
                </Button>
                <Button
                  variant={activeView === 'warehouseReturns' ? 'warning' : 'outline-warning'}
                  className="nav-pill-btn"
                  onClick={() => navigateTo('warehouseReturns')}
                >
                  <i className="bi bi-arrow-return-left me-1"></i>
                  {t('nav.warehouseReturns')}
                </Button>
                <Button
                  variant={activeView === 'adminBillings' ? 'warning' : 'outline-warning'}
                  className="nav-pill-btn"
                  onClick={() => navigateTo('adminBillings')}
                >
                  <i className="bi bi-currency-exchange me-1"></i>
                  {t('nav.adminBillings')}
                </Button>
                <Button
                  variant={activeView === 'adminConsole' ? 'warning' : 'outline-warning'}
                  className="nav-pill-btn"
                  onClick={() => navigateTo('adminConsole')}
                >
                  <i className="bi bi-terminal me-1"></i>
                  {t('nav.adminConsole')}
                </Button>
                <Button
                  variant="outline-warning"
                  className="nav-pill-btn find-return-by-barcode-btn"
                  onClick={() => setShowFindReturnByShkModal(true)}
                >
                  <i className="bi bi-upc-scan me-1"></i>
                  {t('nav.findReturnByShk')}
                </Button>
                <Button
                  variant="outline-danger"
                  className="nav-pill-btn position-relative"
                  onClick={() => setShowHighlightStoresModal(true)}
                  title={t('adminHighlight.intro')}
                >
                  <i className="bi bi-bounding-box me-1"></i>
                  {t('nav.highlightStores')}
                  {highlightedStores.length > 0 && (
                    <Badge
                      bg="danger"
                      pill
                      className="position-absolute"
                      style={{ top: '-5px', right: '-5px', fontSize: '0.65rem' }}
                    >
                      {highlightedStores.length > 99 ? '99+' : highlightedStores.length}
                    </Badge>
                  )}
                </Button>
                {user?.is_superuser && (
                  <Button
                    variant="outline-info"
                    className="nav-pill-btn"
                    onClick={handleOpenImpersonateModal}
                  >
                    <i className="bi bi-person-check me-1"></i>
                    {t('nav.enterAsClient')}
                  </Button>
                )}
                {user?.is_superuser && (
                  <Button
                    variant="outline-warning"
                    className="nav-pill-btn"
                    onClick={() => setShowCreateSuperuserModal(true)}
                  >
                    <i className="bi bi-person-plus me-1"></i>
                    {t('nav.createSuperuser')}
                  </Button>
                )}
              </>
            ) : (
              // Regular user navigation
              <>
                {/* Store Selector */}
                <Dropdown className="store-selector">
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    className="nav-pill-btn d-flex align-items-center gap-2"
                  >
                    {selectedStoreIcon && (
                      <img
                        src={selectedStoreIcon}
                        alt=""
                        style={{ width: 20, height: 20, borderRadius: '4px' }}
                      />
                    )}
                    {getSelectedStoreName()}
                    <i className="bi bi-chevron-down" style={{ fontSize: '0.7rem' }}></i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item 
                      active={!selectedStore}
                      onClick={() => setSelectedStore(null)}
                    >
                      <i className="bi bi-shop me-2"></i>
                      {t('nav.allStores')}
                    </Dropdown.Item>
                    {stores.length > 0 && <Dropdown.Divider />}
                    {stores.map((store) => (
                      <Dropdown.Item
                        key={store.id}
                        active={selectedStore === store.id}
                        onClick={() => setSelectedStore(store.id)}
                        className="d-flex align-items-center"
                      >
                        <img
                          src={getStoreIcon(store.store_type)}
                          alt=""
                          style={{ width: 20, height: 20, borderRadius: '4px', marginRight: '0.5rem' }}
                        />
                        {store.name || `Store ${store.store_id}`}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                <Button
                  variant={activeView === 'goodsOnSale' ? 'warning' : 'outline-secondary'}
                  className="nav-pill-btn"
                  onClick={() => navigateTo('goodsOnSale')}
                >
                  <i className="bi bi-box-seam me-1"></i>
                  {t('nav.goodsOnSale')}
                </Button>

                <Button
                  variant="outline-secondary"
                  className="nav-pill-btn nav-pill-dashed"
                  onClick={() => setShowAddStore(true)}
                >
                  <i className="bi bi-plus-lg me-1"></i>
                  {t('nav.addStore')}
                </Button>
                <Button
                  variant="outline-secondary"
                  className="nav-pill-btn position-relative"
                  onClick={() => navigateTo('billings')}
                >
                  {t('nav.accounts')}
                  {unreadBillingCount > 0 && (
                    <Badge
                      bg="danger"
                      pill
                      className="position-absolute"
                      style={{ top: '-5px', right: '-5px', fontSize: '0.65rem' }}
                    >
                      {unreadBillingCount > 99 ? '99+' : unreadBillingCount}
                    </Badge>
                  )}
                </Button>
                <Button
                  variant="outline-info"
                  className="nav-pill-btn position-relative"
                  onClick={() => navigateTo('support')}
                >
                  <i className="bi bi-chat-dots me-1"></i>
                  {t('nav.support')}
                  {unreadSupportCount > 0 && (
                    <Badge 
                      bg="danger" 
                      pill 
                      className="position-absolute"
                      style={{ top: '-5px', right: '-5px', fontSize: '0.65rem' }}
                    >
                      {unreadSupportCount > 99 ? '99+' : unreadSupportCount}
                    </Badge>
                  )}
                </Button>
                <Button
                  variant="outline-secondary"
                  className="nav-pill-btn"
                  onClick={() => navigateTo('profile')}
                >
                  {t('nav.profile')}
                </Button>
              </>
            )}
          </Nav>

          {/* Right Side - Language, Theme & User */}
          <div className="d-flex align-items-center gap-2">
            {!user?.is_superuser && (
              <SystemNotificationsMenu
                stores={stores}
                onOpenApiKeyModal={(store) => {
                  setUpdatingStore(store);
                  setNewApiKey('');
                  setUpdateApiKeyError('');
                  setUpdateApiKeySuccess('');
                  setShowUpdateApiKey(true);
                }}
              />
            )}
            {/* Language & Theme Selector */}
            <LanguageThemeSelector />

            {/* Mobile Menu */}
            <Dropdown className="d-lg-none">
              <Dropdown.Toggle variant="outline-secondary" className="nav-pill-btn">
                <i className="bi bi-list"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                {user?.is_superuser ? (
                  // Superuser mobile menu
                  <>
                    <Dropdown.Item 
                      active={activeView === 'adminOrders'}
                      onClick={() => navigateTo('adminOrders')}
                    >
                      <i className="bi bi-shield-lock me-2"></i>
                      {t('nav.adminOrders')}
                    </Dropdown.Item>
                    <Dropdown.Item
                      active={activeView === 'adminWbReturns'}
                      onClick={() => navigateTo('adminWbReturns')}
                    >
                      <i className="bi bi-cloud-download me-2"></i>
                      {t('nav.adminWbReturns')}
                    </Dropdown.Item>
                    <Dropdown.Item 
                      active={activeView === 'adminSupport'}
                      onClick={() => navigateTo('adminSupport')}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>
                        <i className="bi bi-headset me-2"></i>
                        {t('nav.adminSupport')}
                      </span>
                      {unreadSupportCount > 0 && (
                        <Badge bg="danger" pill>{unreadSupportCount > 99 ? '99+' : unreadSupportCount}</Badge>
                      )}
                    </Dropdown.Item>
                    <Dropdown.Item 
                      active={activeView === 'warehouseReturns'}
                      onClick={() => navigateTo('warehouseReturns')}
                    >
                      <i className="bi bi-arrow-return-left me-2"></i>
                      {t('nav.warehouseReturns')}
                    </Dropdown.Item>
                    <Dropdown.Item 
                      active={activeView === 'adminBillings'}
                      onClick={() => navigateTo('adminBillings')}
                    >
                      <i className="bi bi-currency-exchange me-2"></i>
                      {t('nav.adminBillings')}
                    </Dropdown.Item>
                    <Dropdown.Item
                      active={activeView === 'adminConsole'}
                      onClick={() => navigateTo('adminConsole')}
                    >
                      <i className="bi bi-terminal me-2"></i>
                      {t('nav.adminConsole')}
                    </Dropdown.Item>
                    <Dropdown.Item className="find-return-by-barcode-btn" onClick={() => { setShowFindReturnByShkModal(true); }}>
                      <i className="bi bi-upc-scan me-2"></i>
                      {t('nav.findReturnByShk')}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setShowHighlightStoresModal(true)}>
                      <i className="bi bi-bounding-box me-2"></i>
                      {t('nav.highlightStores')}
                      {highlightedStores.length > 0 && (
                        <Badge bg="danger" pill className="ms-2">
                          {highlightedStores.length > 99 ? '99+' : highlightedStores.length}
                        </Badge>
                      )}
                    </Dropdown.Item>
                    {user?.is_superuser && (
                      <Dropdown.Item onClick={handleOpenImpersonateModal}>
                        <i className="bi bi-person-check me-2"></i>
                        {t('nav.enterAsClient')}
                      </Dropdown.Item>
                    )}
                    {user?.is_superuser && (
                      <Dropdown.Item onClick={() => setShowCreateSuperuserModal(true)}>
                        <i className="bi bi-person-plus me-2"></i>
                        {t('nav.createSuperuser')}
                      </Dropdown.Item>
                    )}
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={logout} className="text-danger">
                      {t('nav.signOut')}
                    </Dropdown.Item>
                  </>
                ) : (
                  // Regular user mobile menu
                  <>
                    <Dropdown.Header>{t('nav.selectStore')}</Dropdown.Header>
                    <Dropdown.Item 
                      active={!selectedStore}
                      onClick={() => setSelectedStore(null)}
                    >
                      {t('nav.allStores')}
                    </Dropdown.Item>
                    {stores.map((store) => (
                      <Dropdown.Item
                        key={store.id}
                        active={selectedStore === store.id}
                        onClick={() => setSelectedStore(store.id)}
                      >
                        {store.name || `Store ${store.store_id}`}
                      </Dropdown.Item>
                    ))}
                    <Dropdown.Divider />
                    <Dropdown.Item
                      active={activeView === 'goodsOnSale'}
                      onClick={() => navigateTo('goodsOnSale')}
                    >
                      <i className="bi bi-box-seam me-2"></i>
                      {t('nav.goodsOnSale')}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setShowAddStore(true)}>
                      <i className="bi bi-plus-lg me-2"></i>
                      {t('nav.addStore')}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => navigateTo('billings')}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>{t('nav.accounts')}</span>
                      {unreadBillingCount > 0 && (
                        <Badge bg="danger" pill>{unreadBillingCount > 99 ? '99+' : unreadBillingCount}</Badge>
                      )}
                    </Dropdown.Item>
                    <Dropdown.Item 
                      onClick={() => navigateTo('support')}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>
                        <i className="bi bi-chat-dots me-2"></i>
                        {t('nav.support')}
                      </span>
                      {unreadSupportCount > 0 && (
                        <Badge bg="danger" pill>{unreadSupportCount > 99 ? '99+' : unreadSupportCount}</Badge>
                      )}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => navigateTo('profile')}>
                      {t('nav.profile')}
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={logout} className="text-danger">
                      {t('nav.signOut')}
                    </Dropdown.Item>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>

            {/* User Info & Logout (Desktop) */}
            <div className="d-none d-lg-flex align-items-center gap-2 ms-2">
              <span className="text-secondary small">
                {t('common.hello')}, {user?.first_name || user?.email?.split('@')[0] || 'User'}
              </span>
              <Button variant="outline-danger" size="sm" onClick={logout}>
                <i className="bi bi-box-arrow-right"></i>
              </Button>
            </div>
          </div>
        </Container>
      </Navbar>

      {/* Impersonation banner */}
      {isImpersonating() && (
        <Alert variant="warning" className="mb-0 rounded-0 d-flex align-items-center justify-content-between flex-wrap gap-2 py-2">
          <span>
            <i className="bi bi-person-badge me-2"></i>
            {t('impersonate.title')}: {user?.email}
          </span>
          <Button variant="outline-dark" size="sm" onClick={exitImpersonation}>
            <i className="bi bi-box-arrow-right me-1"></i>
            {t('nav.exitImpersonation')}
          </Button>
        </Alert>
      )}

      {/* Main Content */}
      <main className="dashboard-content">{renderContent()}</main>

      {/* Find return by shkId (admin only) */}
      <FindReturnByShkModal
        show={showFindReturnByShkModal}
        onHide={() => setShowFindReturnByShkModal(false)}
        onEditOrder={(orderId) => {
          setActiveView('adminOrders');
          setInitialEditOrderId(orderId);
        }}
      />

      {user?.is_superuser && (
        <SuperuserHighlightStoresModal
          show={showHighlightStoresModal}
          onHide={() => setShowHighlightStoresModal(false)}
          t={t}
        />
      )}

      {user?.is_superuser && (
        <CreateSuperuserModal
          show={showCreateSuperuserModal}
          onHide={() => setShowCreateSuperuserModal(false)}
          t={t}
        />
      )}

      {/* Enter as client (impersonation) modal */}
      <Modal show={showImpersonateModal} onHide={() => setShowImpersonateModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('impersonate.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">{t('impersonate.selectUser')}</p>
          <div className="d-flex gap-2 mb-3">
            <Form.Control
              type="text"
              placeholder={t('impersonate.searchPlaceholder')}
              value={impersonateSearch}
              onChange={(e) => setImpersonateSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), loadImpersonateUsers(impersonateSearch))}
            />
            <Button variant="primary" onClick={() => loadImpersonateUsers(impersonateSearch)} disabled={impersonateLoading}>
              {impersonateLoading ? <Spinner animation="border" size="sm" /> : t('impersonate.search')}
            </Button>
          </div>
          {impersonateError && <Alert variant="danger">{impersonateError}</Alert>}
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {impersonateLoading && !impersonateUsers.length ? (
              <div className="text-center py-4">{t('impersonate.loading')}</div>
            ) : impersonateUsers.length === 0 ? (
              <div className="text-center py-4 text-muted">{t('impersonate.noUsers')}</div>
            ) : (
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>{t('profile.email')}</th>
                    <th>{t('auth.firstName')}</th>
                    <th>{t('auth.lastName')}</th>
                    <th style={{ width: 120 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {impersonateUsers.map((u) => (
                    <tr key={u.id}>
                      <td>{u.email}</td>
                      <td>{u.first_name || '—'}</td>
                      <td>{u.last_name || '—'}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          disabled={impersonateSubmitting}
                          onClick={() => handleImpersonateSelect(u)}
                        >
                          {impersonateSubmitting ? t('impersonate.entering') : t('impersonate.openAsUser')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {/* Add Store Modal */}
      <AddStoreModal
        show={showAddStore}
        onHide={() => setShowAddStore(false)}
        onStoreAdded={handleStoreAdded}
      />

      {/* Edit Store Name Modal */}
      <Modal 
        show={showEditStore} 
        onHide={() => setShowEditStore(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('stores.editTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editStoreError && (
            <Alert variant="danger" dismissible onClose={() => setEditStoreError('')}>
              {editStoreError}
            </Alert>
          )}
          
          {editingStore && (
            <div className="text-center mb-3">
              <img
                src={getStoreIcon(editingStore.store_type)}
                alt={editingStore.store_type}
                style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: '8px' }}
              />
              <div className="text-muted small mt-1">
                {t('stores.storeId')}: {editingStore.store_id}
              </div>
            </div>
          )}

          <Form.Group>
            <Form.Label>{t('stores.storeName')}</Form.Label>
            <Form.Control
              type="text"
              value={editStoreName}
              onChange={(e) => setEditStoreName(e.target.value)}
              placeholder={t('stores.storeNamePlaceholder')}
              autoFocus
            />
            <Form.Text className="text-muted">
              {t('stores.storeNameHint')}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowEditStore(false)}
            disabled={editStoreLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            className="btn-orange"
            onClick={handleSaveStoreName}
            disabled={editStoreLoading}
          >
            {editStoreLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('common.saving')}
              </>
            ) : (
              t('common.save')
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Store Confirmation Modal */}
      <Modal 
        show={showDeleteStore} 
        onHide={() => setShowDeleteStore(false)}
        centered
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="text-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {t('stores.deleteStore')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('stores.deleteStoreConfirm')}</p>
          {deletingStore && (
            <div className="text-center mt-3 p-3 bg-light rounded">
              <img
                src={getStoreIcon(deletingStore.store_type)}
                alt={deletingStore.store_type}
                style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: '8px' }}
              />
              <div className="fw-bold mt-2">{deletingStore.name || `Store ${deletingStore.store_id}`}</div>
              <small className="text-muted">
                {t('stores.storeId')}: {deletingStore.store_id}
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowDeleteStore(false)}
            disabled={deleteStoreLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            variant="danger"
            onClick={confirmDeleteStore}
            disabled={deleteStoreLoading}
          >
            {deleteStoreLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('common.loading')}
              </>
            ) : (
              t('stores.deleteStore')
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Update API Key Modal */}
      <Modal 
        show={showUpdateApiKey} 
        onHide={() => {
          setShowUpdateApiKey(false);
          setUpdatingStore(null);
          setNewApiKey('');
          setUpdateApiKeyError('');
          setUpdateApiKeySuccess('');
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            {t('stores.updateApiKey')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {updateApiKeyError && <Alert variant="danger">{updateApiKeyError}</Alert>}
          {updateApiKeySuccess && <Alert variant="success">{updateApiKeySuccess}</Alert>}
          
          {updatingStore && (
            <div className="mb-3">
              <p className="mb-2">
                <strong>{t('stores.store')}:</strong> {updatingStore.name || `Store ${updatingStore.store_id}`}
              </p>
              {updatingStore.verification_status === 'failed' && (
                <Alert variant="warning" className="mb-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {t('stores.apiKeyInvalidMessage')}
                </Alert>
              )}
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              {t('stores.enterNewApiKey')}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={newApiKey}
              onChange={(e) => setNewApiKey(e.target.value)}
              placeholder={t('stores.apiKeyPlaceholder')}
              className="font-monospace"
              style={{ fontSize: '0.85rem' }}
              disabled={updateApiKeyLoading}
            />
            <Form.Text className="form-hint">
              {t('stores.apiKeyUpdateHint')}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button 
            variant="outline-secondary" 
            onClick={() => {
              setShowUpdateApiKey(false);
              setUpdatingStore(null);
              setNewApiKey('');
              setUpdateApiKeyError('');
              setUpdateApiKeySuccess('');
            }}
            disabled={updateApiKeyLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            className="btn-orange"
            onClick={handleSaveApiKey}
            disabled={!newApiKey.trim() || updateApiKeyLoading}
          >
            {updateApiKeyLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('common.updating')}
              </>
            ) : (
              t('common.update')
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Global Support Chat Notifications Toast Container - Only for regular users, not admins */}
      {/* Temporarily disabled notifications - commented out */}
      {/* {!user?.is_superuser && (
        <ToastContainer 
          position="bottom-end" 
          className="p-3" 
          style={{ zIndex: 1050, position: 'fixed', bottom: 0, right: 0 }}
        >
          {supportNotifications.map((notification) => (
            <Toast 
              key={notification.id}
              show={true}
              onClose={() => dismissSupportNotification(notification.id)}
              className="mb-2 new-message-toast"
              style={{ 
                minWidth: '320px',
                maxWidth: '400px',
                backgroundColor: 'var(--card-bg, #ffffff)',
                border: '2px solid var(--bs-primary)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                opacity: 1,
              }}
              bg="light"
            >
              <Toast.Header 
                className="text-white"
                style={{ 
                  backgroundColor: 'var(--bs-primary)',
                  borderBottom: 'none',
                }}
              >
                <i className="bi bi-chat-dots me-2"></i>
                <strong className="me-auto">{notification.senderName}</strong>
                <small style={{ color: 'rgba(255,255,255,0.8)' }}>{t('support.justNow')}</small>
              </Toast.Header>
              <Toast.Body 
                style={{ 
                  cursor: 'pointer',
                  backgroundColor: 'var(--card-bg, #ffffff)',
                  color: 'var(--bs-body-color)',
                  padding: '1rem',
                }}
                onClick={() => handleSupportNotificationClick(notification.chatId)}
              >
                <div className="small mb-1" style={{ color: 'var(--bs-secondary-color)' }}>
                  {notification.chatSubject}
                </div>
                <div className="fw-medium" style={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  whiteSpace: 'nowrap',
                  color: 'var(--bs-body-color)',
                }}>
                  {notification.message}
                </div>
              <div className="mt-2">
                <Button 
                  size="sm" 
                  variant="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSupportNotificationClick(notification.chatId);
                  }}
                >
                  <i className="bi bi-arrow-right-circle me-1"></i>
                  {t('support.goToChat')}
                </Button>
              </div>
            </Toast.Body>
          </Toast>
          ))}
        </ToastContainer>
      )} */}
      <Footer />
    </div>
  );
};

export default Dashboard;
